import React, { useState, useEffect } from 'react';
import VideoEmbed from '../components/VideoEmbed';
import videoManifest from '../data/videos.json';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Tag,
  CheckCircle,
  AlertTriangle,
  Share2,
  Copy,
  Sparkles,
  Compass
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import Breadcrumbs from '../components/Breadcrumbs';
import { getNavInfo, getRelatedTermsInfo } from '../data/glossaryIndex';
import { isGlossarySlug } from '../data/glossarySlugs';
import { COMPARISON_TITLES } from '../data/comparisonSlugs';
import { fireEvent } from '../utils/funnelEvents';
import GlossaryCTA from '../components/GlossaryCTA';
import { classifyIntent } from '../utils/contentIntent';

// Deterministic per-slug hash so each term gets a stable layout variant.
// Used to break up the identical section skeleton across the glossary
// without rewriting entry content.
const slugHash = (slug) => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) {
    h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return h;
};

// Contextual "Go deeper" links: route glossary readers up the funnel to the
// comparison pages, guides, vertical pages, and the head-term page instead of
// dead-ending in the glossary. First matching rule wins; matched against
// slug + term + category.
const GO_DEEPER_RULES = [
  {
    pattern: /hazel/,
    links: [
      { label: 'Hazel vs Sortio: the direct head-to-head', href: '/hazel-vs-sortio' },
      { label: 'Hazel alternatives for Mac, compared', href: '/compare/hazel-alternative-mac' },
      { label: 'Need Hazel on Windows? What to use instead', href: '/hazel-for-windows' },
    ],
  },
  {
    pattern: /sparkle/,
    links: [
      { label: 'Sparkle vs Sortio, compared honestly', href: '/compare/sparkle-vs-sortio' },
      { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
    ],
  },
  {
    pattern: /juggler|dropit|belvedere|folder.?tidy|spotless|devonthink|alternative/,
    links: [
      { label: 'FileJuggler vs Sortio, compared', href: '/compare/filejuggler-vs-sortio' },
      { label: 'All Sortio comparisons', href: '/compare' },
      { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
    ],
  },
  {
    pattern: /download/,
    links: [
      { label: 'Guide: organize your Downloads folder automatically', href: '/guides/organize-downloads-automatically' },
      { label: 'Keep a Mac Downloads folder clean: 5 strategies', href: '/blog/keep-mac-downloads-folder-clean' },
      { label: 'Copyable prompt: organize Downloads', href: '/prompts/organize-downloads' },
    ],
  },
  {
    pattern: /screenshot/,
    links: [
      { label: 'Tame screenshot chaos on Mac', href: '/blog/tame-screenshot-chaos-mac' },
      { label: 'Copyable prompt: organize screenshots', href: '/prompts/organize-screenshots' },
    ],
  },
  {
    pattern: /medical|patient|health|clinic|hipaa|dental|therap/,
    links: [
      { label: 'Sortio for healthcare (in development)', href: '/for-healthcare' },
    ],
  },
  {
    pattern: /legal|law|matter|paralegal|pleading|discovery|case file/,
    links: [
      { label: 'Sortio for law firms', href: '/for-law-firms' },
      { label: 'Guide: organize legal case files', href: '/guides/organize-legal-case-files' },
      { label: 'How to organize client files by matter', href: '/blog/organize-law-firm-files-by-matter' },
    ],
  },
  {
    pattern: /tax|accounting|accountant|bookkeep|receipt|invoice|audit|payroll|finance/,
    links: [
      { label: 'Sortio for accountants', href: '/for-accountants' },
      { label: 'Guide: organize tax documents', href: '/guides/organize-tax-documents' },
      { label: 'How accounting firms organize client tax documents', href: '/blog/organize-client-tax-documents-accounting-firm' },
    ],
  },
  {
    pattern: /real estate|realtor|property|tenant|lease|transaction/,
    links: [
      { label: 'Sortio for real estate', href: '/for-real-estate' },
      { label: 'Keep transaction files organized across a team', href: '/blog/organize-real-estate-transaction-files' },
    ],
  },
  {
    pattern: /research|academic|citation|thesis|zettelkasten|second.?brain|student/,
    links: [
      { label: 'Sortio for researchers', href: '/for-researchers' },
      { label: 'Guide: organize research papers', href: '/guides/organize-research-papers' },
    ],
  },
  {
    pattern: /photo|image|media|video|music|creative|design|asset/,
    links: [
      { label: 'Guide: manage creative assets', href: '/guides/manage-creative-assets' },
      { label: 'Copyable prompt: photo organization', href: '/prompts/photo-organization' },
    ],
  },
  {
    pattern: /naming|rename/,
    links: [
      { label: 'Enforce a naming convention your team follows', href: '/blog/enforce-file-naming-convention' },
      { label: 'Free tool: file naming convention generator', href: '/tools/file-naming-convention-generator' },
      { label: 'Rename PDFs automatically by content', href: '/blog/automatically-rename-pdfs-by-content' },
    ],
  },
  {
    pattern: /rule|automation|automated|automatic|workflow|smart folder|watch/,
    links: [
      { label: 'Feature: AI Rule Builder', href: '/features/ai-rule-builder' },
      { label: 'AI Sort vs Rule Builder: which to use', href: '/blog/ai-sort-vs-rule-builder-which-to-use' },
      { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
    ],
  },
  {
    pattern: /privacy|security|encrypt|local|offline|ollama|sensitive/,
    links: [
      { label: 'How Sortio handles your data', href: '/security' },
      { label: 'Local AI vs cloud AI for file organization', href: '/blog/local-ai-vs-cloud-ai-file-organization' },
      { label: 'Feature: offline mode', href: '/features/offline-mode' },
    ],
  },
  {
    pattern: /desktop|clutter|declutter|cleanup|duplicate|messy/,
    links: [
      { label: 'Copyable prompt: clean up your Desktop', href: '/prompts/clean-up-desktop' },
      { label: 'Guide: organize files on a Mac, the complete guide', href: '/guides/organize-files-mac-complete-guide' },
    ],
  },
  {
    pattern: /\bai\b|machine learning|llm|nlp|classification|semantic|intelligen/,
    links: [
      { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
      { label: 'Best AI file organizers in 2026, compared', href: '/blog/best-ai-file-organizers' },
    ],
  },
];

const DEFAULT_GO_DEEPER = [
  { label: 'What an AI file organizer actually does', href: '/ai-file-organizer' },
  { label: 'Step-by-step organization guides', href: '/guides' },
  { label: 'How Sortio compares to other tools', href: '/compare' },
];

const getGoDeeperLinks = (term) => {
  const haystack = `${term.slug.replace(/-/g, ' ')} ${term.term} ${term.category}`.toLowerCase();
  const rule = GO_DEEPER_RULES.find((r) => r.pattern.test(haystack));
  const links = rule ? [...rule.links] : [...DEFAULT_GO_DEEPER];
  if (!links.some((l) => l.href === '/ai-file-organizer') && links.length < 4) {
    links.push({ label: 'What an AI file organizer actually does', href: '/ai-file-organizer' });
  }
  return links.slice(0, 4);
};

// Three section-skeleton variants. Heading copy, section order, and list
// layout vary by a stable hash of the slug so neighboring glossary pages do
// not share an identical template.
const buildLayout = (term) => {
  const v = slugHash(term.slug) % 3;
  const name = term.term;
  if (v === 1) {
    return {
      variant: v,
      headings: {
        whatIs: `${name}, explained`,
        howItWorks: `How ${name.toLowerCase().startsWith('how') ? 'it' : name} works in practice`,
        benefits: `Why ${name} matters`,
        bestPractices: 'Best practices',
        challenges: 'Common challenges and fixes',
        sortio: 'Where Sortio fits',
      },
      order: ['whatIs', 'howItWorks', 'benefits', 'challenges', 'bestPractices'],
      benefitsTwoCol: true,
      practicesNumbered: false,
      sortioCopy: `If ${name.toLowerCase()} is the problem you are wrestling with, Sortio is built for it. Type a prompt like "organize these by client and year", review the proposed moves, then apply. Rule-based sorting, semantic search, and file chat are free and unlimited, and every sort can be undone.`,
      ctaLabel: 'Try Sortio on a real folder',
    };
  }
  if (v === 2) {
    return {
      variant: v,
      headings: {
        whatIs: `What ${name} means`,
        howItWorks: `${name} in practice`,
        benefits: `Benefits of ${name}`,
        bestPractices: `Getting ${name} right`,
        challenges: 'Where it goes wrong (and how to fix it)',
        sortio: 'Putting this into practice with Sortio',
      },
      order: ['whatIs', 'howItWorks', 'challenges', 'benefits', 'bestPractices'],
      benefitsTwoCol: false,
      practicesNumbered: true,
      sortioCopy: `You do not need to master ${name.toLowerCase()} by hand. Sortio reads file names, metadata, and (when you enable the content toggle) document contents, then proposes an organization plan you approve before any file moves. One-click undo covers the rest.`,
      ctaLabel: 'Get Sortio for Mac or Windows',
    };
  }
  return {
    variant: v,
    headings: {
      whatIs: `What is ${name}?`,
      howItWorks: `How ${name} works`,
      benefits: `Benefits of ${name}`,
      bestPractices: `${name} best practices`,
      challenges: `Common ${name} challenges and solutions`,
      sortio: `How Sortio handles ${name}`,
    },
    order: ['whatIs', 'howItWorks', 'benefits', 'bestPractices', 'challenges'],
    benefitsTwoCol: false,
    practicesNumbered: true,
    sortioCopy: `Sortio applies the ideas behind ${name.toLowerCase()} directly: describe how you want files organized in plain English and it sorts, renames, and files them for you, with a preview before anything moves and one-click undo after. The free tier includes a one-time AI trial allowance, and rule-based sorting is free and unlimited.`,
    ctaLabel: 'Download Sortio Free',
  };
};

const GlossaryTermPage = () => {
  const { termSlug } = useParams();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // One ~6KB JSON module per term (emitted by generate-glossary-data.js)
    // instead of the whole glossaryData chunk. That chunk gzips to 676KB, and
    // because this page hydrates into its loading state, every glossary
    // visitor watched the prerendered article vanish into "Loading..." until
    // all of it had downloaded and parsed.
    import(`../data/glossary-terms/${termSlug}.json`)
      .then((mod) => {
        if (!cancelled) {
          setTerm(mod.default || mod);
          setLoading(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (isGlossarySlug(termSlug)) {
          // The term exists; the chunk fetch failed (deploy raced a cached
          // page, network blip). Stay on the loading screen rather than
          // bouncing a real URL to the hub.
          setLoading(true);
        } else {
          setTerm(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [termSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#121212" }}>
        <div className="text-apple-400 text-lg">Loading...</div>
      </div>
    );
  }

  if (!term) {
    return <Navigate to="/glossary" replace />;
  }

  const { prev: prevTerm, next: nextTerm } = getNavInfo(termSlug);
  const baseLayout = buildLayout(term);
  // A term written as a question or how-to ("Best Way to Organize Computer
  // Files") reads badly in the generated "What is X?" headings, so an entry
  // can name its own section headings.
  const layout = term.headings ? { ...baseLayout, headings: { ...baseLayout.headings, ...term.headings } } : baseLayout;
  const goDeeperLinks = getGoDeeperLinks(term);
  // Search intent behind the slug decides how hard the above-the-fold CTA
  // pushes. See utils/contentIntent.js for the funnel data behind the split.
  const intent = classifyIntent(term.slug, term.term);

  // Rendered in a different slot depending on intent, so it lives in a
  // variable rather than inline in the content flow.
  const sortioSection = (
    <section id="sortio-integration" className="mb-12">
      <div className="bg-gradient-to-r from-sortio-primary/10 to-sortio-secondary/10 rounded-2xl p-8 border border-sortio-primary/20">
        <div className="flex items-center mb-4">
          <Sparkles className="text-sortio-primary mr-3" size={24} />
          <h2 className="text-2xl font-semibold text-apple-50">{layout.headings.sortio}</h2>
        </div>
        <p className="text-apple-300 mb-6 leading-relaxed">
          {layout.sortioCopy}
        </p>
        <Link
          to="/download"
          onClick={() => fireEvent('cta_click', { cta_id: 'glossary_term_sortio_block', page: `/glossary/${term.slug}`, target: '/download', intent })}
          className="inline-flex items-center px-6 py-3 bg-sortio-primary hover:bg-sortio-primary/90 text-white font-medium rounded-xl transition-colors duration-200"
        >
          {layout.ctaLabel}
          <ArrowRight className="ml-2" size={16} />
        </Link>
      </div>
    </section>
  );
  // Render-time filter: only related terms that actually exist in the index
  // produce cards, and the section disappears entirely when none resolve.
  const relatedTermsInfo = getRelatedTermsInfo((term.relatedTerms || []).slice(0, 6));
  // Competitor comparisons that used to live in this glossary and now sit at
  // /compare/. Filtered through the generated manifest so a retired slug
  // cannot render a link to a 404.
  const relatedComparisons = (term.relatedComparisons || [])
    .filter((slug) => COMPARISON_TITLES[slug])
    .slice(0, 4);

  const breadcrumbItems = [
    { label: 'Glossary', href: '/glossary' },
    { label: term.category, href: null },
    { label: term.term, href: null }
  ];

  const termSchema = {
    "@context": "https://schema.org",
    "@type": "DefinedTerm",
    "name": term.term,
    "description": term.definition,
    "inDefinedTermSet": {
      "@type": "DefinedTermSet",
      "name": "Sortio File Organization Glossary",
      "url": "https://www.getsortio.com/glossary"
    },
    "url": `https://www.getsortio.com/glossary/${term.slug}`,
    "dateModified": term.lastUpdated,
    "publisher": {
      "@type": "Organization",
      "name": "Sortio",
      "url": "https://www.getsortio.com"
    }
  };

  const faqSchema = term.faq && term.faq.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": term.faq.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  const schemas = [termSchema];
  if (faqSchema) schemas.push(faqSchema);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${term.term} - Definition & Guide | Sortio Glossary`,
          text: term.shortDescription,
          url: window.location.href,
        });
      } catch (err) {
        console.error('Error sharing:', err);
      }
    } else {
      handleCopyLink();
    }
  };

  const pageTitle = term.seoTitle ?? `${term.term} - Definition & Guide | Sortio Glossary`;
  const pageDescription = term.seoDescription ?? `Learn what ${term.term} means in file organization. Discover how ${term.term} helps manage documents efficiently with AI-powered tools like Sortio.`;

  // Section renderers keyed by section id so the order can vary per layout.
  const sectionRenderers = {
    whatIs: () => term.content.whatIs && (
      <React.Fragment key="what-is-frag">
      <section key="what-is" id="what-is" className="mb-12">
        <h2 className="text-2xl font-semibold text-apple-50 mb-6">{layout.headings.whatIs}</h2>
        <div className="text-apple-300 leading-relaxed space-y-4">
          {term.content.whatIs.split('\n\n').map((paragraph, index) =>
            paragraph.includes('\u251c') || paragraph.includes('\u2514') ? (
              <pre key={index} className="whitespace-pre overflow-x-auto font-mono text-sm bg-apple-800/40 border border-apple-700/40 rounded-xl p-4">{paragraph}</pre>
            ) : (
              <p key={index}>{paragraph}</p>
            )
          )}
        </div>
      </section>
      </React.Fragment>
    ),
    howItWorks: () => term.content.howItWorks && (
      <section key="how-it-works" id="how-it-works" className="mb-12">
        <h2 className="text-2xl font-semibold text-apple-50 mb-6">{layout.headings.howItWorks}</h2>
        <div className="text-apple-300 leading-relaxed space-y-4">
          {term.content.howItWorks.split('\n\n').map((paragraph, index) =>
            paragraph.includes('\u251c') || paragraph.includes('\u2514') ? (
              <pre key={index} className="whitespace-pre overflow-x-auto font-mono text-sm bg-apple-800/40 border border-apple-700/40 rounded-xl p-4">{paragraph}</pre>
            ) : (
              <p key={index}>{paragraph}</p>
            )
          )}
        </div>
      </section>
    ),
    benefits: () => term.content.benefits && term.content.benefits.length > 0 && (
      <section key="benefits" id="benefits" className="mb-12">
        <h2 className="text-2xl font-semibold text-apple-50 mb-6">{layout.headings.benefits}</h2>
        <div className={layout.benefitsTwoCol ? 'grid md:grid-cols-2 gap-4' : 'grid gap-4'}>
          {term.content.benefits.map((benefit, index) => (
            <div key={index} className="flex items-start space-x-3">
              <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
              <span className="text-apple-300">{benefit}</span>
            </div>
          ))}
        </div>
      </section>
    ),
    bestPractices: () => term.content.bestPractices && term.content.bestPractices.length > 0 && (
      <section key="best-practices" id="best-practices" className="mb-12">
        <h2 className="text-2xl font-semibold text-apple-50 mb-6">{layout.headings.bestPractices}</h2>
        <div className="grid gap-4">
          {term.content.bestPractices.map((practice, index) => (
            <div key={index} className="flex items-start space-x-3">
              {layout.practicesNumbered ? (
                <div className="w-6 h-6 bg-sortio-primary/20 text-sortio-primary rounded-full flex items-center justify-center text-sm font-medium mt-0.5 flex-shrink-0">
                  {index + 1}
                </div>
              ) : (
                <CheckCircle className="text-sortio-primary mt-1 flex-shrink-0" size={16} />
              )}
              <span className="text-apple-300">{practice}</span>
            </div>
          ))}
        </div>
      </section>
    ),
    challenges: () => term.content.challenges && term.content.challenges.length > 0 && (
      <section key="challenges" id="challenges" className="mb-12">
        <h2 className="text-2xl font-semibold text-apple-50 mb-6">{layout.headings.challenges}</h2>
        <div className="space-y-6">
          {term.content.challenges.map((item, index) => (
            <div key={index} className="bg-apple-700/20 rounded-xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <AlertTriangle className="text-amber-400 mt-1 flex-shrink-0" size={16} />
                <div>
                  <h4 className="font-medium text-apple-50 mb-2">Challenge:</h4>
                  <p className="text-apple-300">{item.challenge}</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                <div>
                  <h4 className="font-medium text-apple-50 mb-2">Solution:</h4>
                  <p className="text-apple-300">{item.solution}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    ),
  };

  const tocAnchors = {
    whatIs: '#what-is',
    howItWorks: '#how-it-works',
    benefits: '#benefits',
    bestPractices: '#best-practices',
    challenges: '#challenges',
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#121212" }}>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={`https://www.getsortio.com/glossary/${term.slug}`}
        schema={schemas}
        keywords={`${term.term}, file organization, document management, ${term.category.toLowerCase()}, digital filing`}
        lastModified={term.lastUpdated}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Header Section */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/glossary"
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Glossary
            </Link>

            <div className="flex items-center gap-2">
              <button
                onClick={handleShare}
                className="flex items-center px-3 py-2 bg-apple-700/40 text-apple-300 hover:text-sortio-primary rounded-lg transition-colors"
                title="Share this term"
              >
                <Share2 size={16} className="mr-1" />
                Share
              </button>
              <button
                onClick={handleCopyLink}
                className="flex items-center px-3 py-2 bg-apple-700/40 text-apple-300 hover:text-sortio-primary rounded-lg transition-colors"
                title="Copy link"
              >
                {copiedToClipboard ? (
                  <>
                    <CheckCircle size={16} className="mr-1 text-green-400" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} className="mr-1" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mb-4">
            <span className="inline-block px-3 py-1 bg-sortio-primary/10 text-sortio-primary text-sm font-medium rounded-lg">
              {term.category}
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl font-semibold mb-6 text-apple-50">
            {term.term}
          </h1>

          <div className="bg-sortio-primary/10 border border-sortio-primary/20 rounded-2xl p-6 mb-8">
            <p className="text-lg text-apple-100 leading-relaxed">
              {term.definition}
            </p>
          </div>

          {/* Above-the-fold conversion block. The only CTA on this template
              used to sit near the bottom of a 668-line page; this one sits
              where the reader actually is. */}
          <GlossaryCTA intent={intent} page={`/glossary/${term.slug}`} placement="hero" />

          <div className="flex items-center gap-6 text-sm text-apple-400">
            <div className="flex items-center">
              <Clock className="mr-2" size={16} />
              Last updated: {new Date(term.lastUpdated).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Tag className="mr-2" size={16} />
              {term.category}
            </div>
          </div>
        </div>

        {/* Video sits directly under the hero, above the table of contents.
            Google requires the video to be the prominent main content of a
            "watch page"; buried inside the whatIs section it was rejected. */}
        {videoManifest[`/glossary/${term.slug}`] && (
          <VideoEmbed video={videoManifest[`/glossary/${term.slug}`]} />
        )}

        {/* Table of Contents */}
        <div className="mb-12 bg-apple-700/20 rounded-2xl p-6">
          <h3 className="text-lg font-medium text-apple-50 mb-4">Table of Contents</h3>
          <nav className="space-y-2">
            {layout.order.map((key) => (
              term.content[key] && (
                <a key={key} href={tocAnchors[key]} className="block text-apple-300 hover:text-sortio-primary transition-colors">
                  {layout.headings[key]}
                </a>
              )
            ))}
            <a href="#sortio-integration" className="block text-apple-300 hover:text-sortio-primary transition-colors">
              {layout.headings.sortio}
            </a>
            {term.faq && term.faq.length > 0 && (
              <a href="#faq" className="block text-apple-300 hover:text-sortio-primary transition-colors">
                Frequently Asked Questions
              </a>
            )}
          </nav>
        </div>

        {/* Main Content. Section order is answer-first on every intent: a
            reader who searched "hazel vs dropit" came for the comparison, and
            leading with the pitch instead of the answer raises bounce. The
            product ask lives above the fold (GlossaryCTA) and again as the
            closing block below. */}
        <div className="prose prose-invert max-w-none">
          {layout.order.map((key) => sectionRenderers[key]())}
          {sortioSection}
        </div>

        {/* FAQ Section */}
        {term.faq && term.faq.length > 0 && (
          <section id="faq" className="mb-12">
            <h2 className="text-2xl font-semibold text-apple-50 mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              {term.faq.map((faq, index) => (
                <div key={index} className="bg-apple-700/20 rounded-xl p-6">
                  <h3 className="font-medium text-apple-50 mb-3">{faq.question}</h3>
                  <p className="text-apple-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Go deeper: contextual links up the funnel */}
        <section className="mb-12">
          <div className="bg-apple-700/20 border border-apple-600/30 rounded-2xl p-6">
            <div className="flex items-center mb-4">
              <Compass className="text-sortio-primary mr-3" size={20} />
              <h3 className="text-xl font-semibold text-apple-50">Go deeper</h3>
            </div>
            <div className="grid gap-3">
              {goDeeperLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="flex items-center justify-between p-3 bg-apple-700/30 rounded-xl hover:bg-apple-600/30 transition-colors group"
                >
                  <span className="text-apple-200 group-hover:text-sortio-primary transition-colors">
                    {link.label}
                  </span>
                  <ArrowRight className="text-apple-400 group-hover:text-sortio-primary group-hover:translate-x-1 transition-all flex-shrink-0 ml-3" size={16} />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {relatedComparisons.length > 0 && (
          <section className="mb-12">
            <h3 className="text-xl font-semibold text-apple-50 mb-6">Compare the tools</h3>
            <div className="flex flex-wrap gap-3">
              {relatedComparisons.map((slug) => (
                <Link
                  key={slug}
                  to={`/compare/${slug}`}
                  className="px-4 py-2 bg-apple-700/30 text-apple-200 hover:text-sortio-primary hover:bg-apple-600/30 rounded-lg transition-colors text-sm"
                >
                  {COMPARISON_TITLES[slug]}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Related Terms (only terms that resolve in the index render) */}
        {relatedTermsInfo.length > 0 && (
          <section className="mb-12">
            <h3 className="text-xl font-semibold text-apple-50 mb-6">Related Terms</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {relatedTermsInfo.map((relatedTerm, index) => (
                  <Link
                    key={index}
                    to={`/glossary/${relatedTerm.slug}`}
                    className="block p-4 bg-apple-700/20 rounded-xl hover:bg-apple-600/30 transition-colors group"
                  >
                    <h4 className="font-medium text-apple-50 group-hover:text-sortio-primary transition-colors mb-2">
                      {relatedTerm.term}
                    </h4>
                    <p className="text-sm text-apple-400 line-clamp-2">
                      {relatedTerm.shortDescription}
                    </p>
                  </Link>
                ))}
            </div>
          </section>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center border-t border-apple-600/30 pt-8">
          {prevTerm ? (
            <Link
              to={`/glossary/${prevTerm.slug}`}
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors group"
            >
              <ArrowLeft className="mr-2 group-hover:-translate-x-1 transition-transform" size={16} />
              <div>
                <div className="text-xs uppercase tracking-wider mb-1">Previous</div>
                <div className="font-medium">{prevTerm.term}</div>
              </div>
            </Link>
          ) : (
            <div></div>
          )}

          {nextTerm ? (
            <Link
              to={`/glossary/${nextTerm.slug}`}
              className="flex items-center text-apple-400 hover:text-sortio-primary transition-colors group text-right"
            >
              <div>
                <div className="text-xs uppercase tracking-wider mb-1">Next</div>
                <div className="font-medium">{nextTerm.term}</div>
              </div>
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
            </Link>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlossaryTermPage;
